import { Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { DatePicker } from 'antd/lib'
import dayjs from 'dayjs'
import { useState } from 'react'

import { useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint } from 'src/api/queries'
import {
  PregnancyMonitoringPlanInvestigationItemDto,
  PregnancyMonitoringPlanInvestigationStatus,
  PregnancyPeriod
} from 'src/api/requests'
import { PageBreadcrumbs, Select } from 'src/components'
import {
  PregnancyInvestigationsStatusTag,
  PregnancyPeriodStatusTag
} from 'src/features/persons/components'
import { useTablePagination, useUser, useDefaultSortingItem } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormatWithDot
} from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

export const InstitutionPregnancyInvestigations = () => {
  const { t } = useLocales()
  const { userData } = useUser()

  const pagination = useTablePagination()

  const [pregnancyPeriod, setPregnancyPeriod] = useState('')
  const [date, setDate] = useState<string[]>([])
  const [status, setStatus] = useState('')

  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current ?? 1,
    pageSize: pagination.pageSize ?? 10,
    institutionIds: [String(userData?.currentLegalEntityId)],
    ...(pregnancyPeriod && {
      pregnancyPeriod: [pregnancyPeriod] as PregnancyPeriod[]
    }),
    ...(status && {
      status: [status] as PregnancyMonitoringPlanInvestigationStatus[]
    }),
    ...(date?.[0] &&
      date?.[1] && {
        periodDateFrom: date?.[0],
        periodDateTo: date?.[1]
      })
  }

  const { data, isLoading } =
    useTreatmentServiceGetListPregnancyMonitoringInvestigationsEndpoint(
      queryParams
    )

  const onPregnancyPeriodSelect = (val: string) => {
    setPregnancyPeriod(val)
    resetPagination(pagination)
  }

  const onStatusSelect = (val: string) => {
    setStatus(val)
    resetPagination(pagination)
  }

  const pregnancyPeriodOptions = [
    defaultSortingOption,
    ...Object.values(PregnancyPeriod).map((item) => ({
      value: item,
      label: t(`pregnancy.period_status.${item}`)
    }))
  ]

  const pregnancyStatusOptions = [
    defaultSortingOption,
    ...Object.values(PregnancyMonitoringPlanInvestigationStatus).map(
      (item) => ({
        value: item,
        label: t(`pregnancy.investigation_status.${item}`)
      })
    )
  ]

  const columns: ColumnsType<PregnancyMonitoringPlanInvestigationItemDto> = [
    {
      key: 'created date',
      title: t('investigations.created_date'),
      render: ({ periodDate }: { periodDate: string }) =>
        dayjs.utc(periodDate).tz().format(dayMonthYearHourMinutesFormatWithDot)
    },
    {
      key: 'pregnancyPeriod',
      title: t('pregnancy.monitoring_period'),
      render: (_, { pregnancyPeriod }) => (
        <PregnancyPeriodStatusTag
          status={pregnancyPeriod ?? PregnancyPeriod.INITIAL}
        />
      )
    },
    {
      key: 'status',
      title: t('form.status'),
      render: (_, { status }) => (
        <PregnancyInvestigationsStatusTag status={status} />
      )
    }
  ]

  return (
    <PageBreadcrumbs
      items={[{ title: t('persons.person_pregnancy') }]}
      title={t('menu.investigations')}
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <DatePicker.RangePicker
            format={dayMonthYearFormatWithDot}
            onChange={(_, dateStrings) => setDate(dateStrings)}
            placeholder={[t('specimens.created_by'), t('specimens.created_at')]}
          />
          <Select
            onSelect={onPregnancyPeriodSelect}
            options={pregnancyPeriodOptions}
            placeholder={t('pregnancy.pregnancy_type')}
          />

          <Select
            onSelect={onStatusSelect}
            options={pregnancyStatusOptions}
            placeholder={t('form.status')}
          />
        </Flex>
        <Table
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
